import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Download, RefreshCw, AlertTriangle, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import jsPDF from 'jspdf';
import { WILDNET_LOGO_PNG, WILDNET_LOGO_RATIO } from '@/lib/logo';
import AnalysisText from '@/components/AnalysisText';

const DIMENSION_LABELS = {
  accountability: 'Accountability',
  integrity: 'Integrity',
  problem_solving: 'Problem Solving',
  emotional_maturity: 'Emotional Maturity',
  social_empathy: 'Social & Empathy',
  motivation_values: 'Motivation & Values',
  leadership: 'Leadership',
};

const RadarChart = ({ scores, size = 300 }) => {
  const dims = Object.entries(scores).filter(([k]) => k in DIMENSION_LABELS);
  const n = dims.length;
  if (n < 3) return null;

  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.38;
  const angleStep = (2 * Math.PI) / n;

  const getPoint = (index, value) => {
    const angle = angleStep * index - Math.PI / 2;
    const dist = (value / 100) * r;
    return { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) };
  };

  const gridLevels = [20, 40, 60, 80, 100];
  const dataPoints = dims.map(([, score], i) => getPoint(i, score));
  const pathData = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
      {/* Grid */}
      {gridLevels.map((level) => {
        const points = dims.map((_, i) => getPoint(i, level));
        const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
        return <path key={level} d={d} fill="none" stroke="#e5e7eb" strokeWidth="1" />;
      })}
      {/* Axes */}
      {dims.map(([, ], i) => {
        const end = getPoint(i, 100);
        return <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="#e5e7eb" strokeWidth="1" />;
      })}
      {/* Data */}
      <path d={pathData} fill="rgb(var(--brand-rgb) / 0.15)" stroke="var(--brand)" strokeWidth="2" />
      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="4" fill="var(--brand)" />
      ))}
      {/* Labels */}
      {dims.map(([dim, score], i) => {
        const labelPoint = getPoint(i, 115);
        return (
          <text key={dim} x={labelPoint.x} y={labelPoint.y} textAnchor="middle" dominantBaseline="middle" className="text-[10px] fill-neutral-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {DIMENSION_LABELS[dim]} ({score})
          </text>
        );
      })}
    </svg>
  );
};

const ScoreBar = ({ label, score, labelText }) => {
  const color = score >= 80 ? '#1D9E75' : score >= 60 ? 'var(--brand)' : score >= 40 ? '#EF9F27' : '#E24B4A';
  return (
    <div className="mb-3">
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-neutral-700">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">{labelText}</span>
          <span className="text-sm font-bold text-neutral-900">{score}</span>
        </div>
      </div>
      <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

// The PDF is plain text, so Markdown syntax would be printed literally.
// Strip the marks and keep the words.
const stripMarkdown = (text) => (text || '')
  .replace(/^\s*#{1,6}\s+/gm, '')      // headings
  .replace(/\*\*(.+?)\*\*/g, '$1')    // bold
  .replace(/\*(.+?)\*/g, '$1')         // italics
  .replace(/`(.+?)`/g, '$1')           // code
  .replace(/^\s*[-*]\s+/gm, '- ')      // bullets
  .replace(/\|/g, ' ')                 // table pipes
  .replace(/^\s*[-: ]{3,}\s*$/gm, '')  // table rules
  .replace(/\n{3,}/g, '\n\n')          // collapse blank runs
  .trim();

const ReportView = () => {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [manualText, setManualText] = useState('');
  const [savingManual, setSavingManual] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => { loadReport(); }, [assessmentId]);

  const loadReport = async () => {
    try {
      const res = await api.getFullReport(assessmentId);
      setData(res.data);
    } catch { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const handleRegenerateAI = async () => {
    setRegenerating(true);
    try {
      await api.regenerateAI(assessmentId);
      await loadReport();
      toast.success('AI analysis regenerated');
    } catch { toast.error('Failed to regenerate'); }
    finally { setRegenerating(false); }
  };

  const exportPDF = () => {
    if (!data) return;
    const { assessment: a, scorecard: sc } = data;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();

    // Logo first. If it ever fails to draw, the report is still produced -
    // a missing image must not cost someone their export.
    const logoW = 38;
    const logoH = logoW / WILDNET_LOGO_RATIO;
    try {
      doc.addImage(WILDNET_LOGO_PNG, 'PNG', 20, 12, logoW, logoH);
    } catch (e) {
      console.warn('Logo could not be added to the PDF', e);
    }

    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('CONFIDENTIAL', pw - 40, 16);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, pw - 40, 21);

    // Was a leftover purple from the original build, matching nothing.
    doc.setFontSize(15);
    doc.setTextColor(36, 52, 66);
    doc.text('Candidate Assessment Report', 20, 34);

    doc.setDrawColor(225, 225, 225);
    doc.line(20, 38, pw - 20, 38);

    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text(`Candidate: ${a?.candidate_name || 'N/A'}`, 20, 47);
    doc.text(`Role: ${a?.role_applied_for || 'N/A'}`, 20, 54);
    doc.text(`Version: ${a?.version || 'N/A'}`, 20, 61);
    doc.text(`Consistency: ${sc?.consistency_score ?? 'N/A'}%`, 20, 68);

    let y = 80;
    doc.setFontSize(12);
    doc.text('Dimension Scores', 20, y);
    doc.setFontSize(9);
    y += 8;

    if (sc?.dimension_scores) {
      Object.entries(sc.dimension_scores).forEach(([dim, score]) => {
        const label = DIMENSION_LABELS[dim] || dim;
        doc.text(`${label}: ${score}/100 (${sc.dimension_labels?.[dim] || ''})`, 25, y);
        y += 6;
      });
    }

    if (sc?.red_flags?.length) {
      y += 8;
      doc.setFontSize(12);
      doc.text('Red Flags', 20, y);
      doc.setFontSize(9);
      y += 8;
      sc.red_flags.forEach(f => {
        doc.text(`- ${f.flag}`, 25, y);
        y += 6;
      });
    }

    if (sc?.ai_analysis) {
      const ai = sc.ai_analysis;
      if (ai.personality_summary) {
        y += 8;
        doc.setFontSize(12);
        doc.text('Personality Summary', 20, y);
        y += 8;
        doc.setFontSize(9);
        const lines = doc.splitTextToSize(stripMarkdown(ai.personality_summary), pw - 40);
        doc.text(lines, 20, y);
        y += lines.length * 5 + 4;
      }

      if (ai.recommendation_verdict) {
        y += 4;
        doc.setFontSize(12);
        doc.text(`Recommendation: ${ai.recommendation_verdict}`, 20, y);
      }
    }

    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    doc.text('CONFIDENTIAL - For internal use only', 20, 285);

    doc.save(`report-${a?.candidate_name || 'candidate'}.pdf`);
    toast.success('PDF downloaded');
  };

  const copyAnalysisPrompt = async () => {
    try {
      const res = await api.getAnalysisPrompt(assessmentId);
      await navigator.clipboard.writeText(res.data.prompt);
      toast.success('Prompt copied - paste it into claude.ai');
    } catch (err) {
      toast.error('Could not build the prompt');
    }
  };

  const submitManualAnalysis = async () => {
    if (manualText.trim().length < 100) {
      toast.error("That looks too short - paste Claude's whole reply");
      return;
    }
    setSavingManual(true);
    try {
      const res = await api.saveManualAnalysis(assessmentId, manualText);
      toast.success(`Analysis saved - ${res.data.verdict}`);
      setManualText('');
      setShowManual(false);
      loadReport();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the analysis');
    } finally {
      setSavingManual(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" />
    </div>
  );

  if (!data || !data.scorecard) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
      <div className="text-center">
        <p className="text-neutral-600 mb-4">Report not available yet.</p>
        <Button onClick={() => navigate('/dashboard')} className="bg-brand hover:bg-brand-dark rounded-md">Back to Dashboard</Button>
      </div>
    </div>
  );

  const { assessment: a, scorecard: sc, integrity_events: events } = data;
  const ai = sc?.ai_analysis || {};
  // True when no written analysis exists yet - either no API key is configured,
  // or the call failed. The scores and charts above are unaffected.
  const hasAnalysis = Boolean(ai.personality_summary || ai.final_recommendation || ai.raw);

  const verdict = ai.recommendation_verdict || 'N/A';

  const verdictColors = {
    'STRONG HIRE': 'bg-emerald-100 text-emerald-800 border-emerald-300',
    'CONDITIONAL HIRE': 'bg-amber-100 text-amber-800 border-amber-300',
    'DO NOT HIRE': 'bg-red-100 text-red-800 border-red-300',
  };

  const flagIcon = (severity) => {
    if (severity === 'red') return <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />;
    if (severity === 'yellow') return <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />;
    return <AlertCircle className="h-4 w-4 text-neutral-400 flex-shrink-0" />;
  };

  return (
    <div className="min-h-screen bg-[#F9F9F6] py-6 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-md mb-3" data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="report-title">
              {a?.candidate_name} - Assessment Report
            </h1>
            <p className="text-sm text-neutral-500 mt-1">{a?.role_applied_for} | {a?.version === 'senior' ? 'Senior' : 'Junior'} | {a?.submitted_at ? new Date(a.submitted_at).toLocaleString() : 'N/A'}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRegenerateAI} variant="outline" className="rounded-md" disabled={regenerating} data-testid="regenerate-ai-button">
              <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} /> Regenerate AI
            </Button>
            <Button onClick={exportPDF} className="bg-brand hover:bg-brand-dark text-white rounded-md" data-testid="export-pdf-button">
              <Download className="h-4 w-4 mr-2" /> PDF
            </Button>
          </div>
        </div>

        {/* Recommendation Banner */}
        <div className={`p-4 rounded-lg border mb-6 ${verdictColors[verdict] || 'bg-neutral-100 text-neutral-800 border-neutral-200'}`} data-testid="recommendation-banner">
          <div className="flex items-center gap-3">
            {verdict === 'STRONG HIRE' && <CheckCircle className="h-6 w-6" />}
            {verdict === 'CONDITIONAL HIRE' && <AlertTriangle className="h-6 w-6" />}
            {verdict === 'DO NOT HIRE' && <XCircle className="h-6 w-6" />}
            <div>
              <p className="font-bold text-lg">{verdict}</p>
              <p className="text-sm opacity-80 mt-0.5">AI-generated recommendation based on assessment analysis</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Radar Chart */}
          <div className="bg-white rounded-lg border border-neutral-200 p-6">
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Dimension Overview</h2>
            {sc?.dimension_scores && <RadarChart scores={sc.dimension_scores} />}
          </div>

          {/* Key Metrics */}
          <div className="bg-white rounded-lg border border-neutral-200 p-6">
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Key Metrics</h2>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-neutral-50 rounded-md">
                <p className="text-xs text-neutral-500">Consistency</p>
                <p className={`text-xl font-bold ${(sc?.consistency_score ?? 100) < 60 ? 'text-red-600' : 'text-neutral-900'}`}>{sc?.consistency_score ?? 'N/A'}%</p>
              </div>
              <div className="p-3 bg-neutral-50 rounded-md">
                <p className="text-xs text-neutral-500">Avg Response Time</p>
                <p className={`text-xl font-bold ${(sc?.avg_response_time ?? 0) < 8 ? 'text-amber-600' : 'text-neutral-900'}`}>{sc?.avg_response_time ?? 0}s</p>
              </div>
              <div className="p-3 bg-neutral-50 rounded-md">
                <p className="text-xs text-neutral-500">Personality Type</p>
                <p className="text-xl font-bold text-brand">{sc?.personality_tag || 'N/A'}</p>
              </div>
              <div className="p-3 bg-neutral-50 rounded-md">
                <p className="text-xs text-neutral-500">Fast Answers (&lt;8s)</p>
                <p className={`text-xl font-bold ${(sc?.fast_answer_count ?? 0) > 5 ? 'text-amber-600' : 'text-neutral-900'}`}>{sc?.fast_answer_count ?? 0}</p>
              </div>
            </div>

            {/* Integrity Events */}
            {events && events.length > 0 && (
              <div>
                <p className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-2">Integrity Events</p>
                <div className="space-y-1">
                  {events.map((ev, i) => (
                    <div key={i} className="flex justify-between text-xs p-1.5 bg-neutral-50 rounded">
                      <span className="text-neutral-600">{ev.event_type.replace(/_/g, ' ')}</span>
                      <span className="font-bold text-neutral-900">{ev.event_count}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Dimension Scores */}
        <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
          <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Dimension Scores</h2>
          {sc?.dimension_scores && Object.entries(sc.dimension_scores).map(([dim, score]) => (
            <ScoreBar key={dim} label={DIMENSION_LABELS[dim] || dim} score={score} labelText={sc.dimension_labels?.[dim] || ''} />
          ))}
        </div>

        {/* Red Flags */}
        {sc?.red_flags?.length > 0 && (
          <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Red Flags & Warnings</h2>
            <div className="space-y-2">
              {sc.red_flags.map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-neutral-50 rounded-md" data-testid={`flag-${i}`}>
                  {flagIcon(f.severity)}
                  <span className="text-sm text-neutral-700">{f.flag}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Integrity Score */}
        {data?.integrity_summary && (
          <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6" data-testid="integrity-score-section">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-5 w-5 text-accent2" />
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Session Integrity</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="p-3 bg-neutral-50 rounded-md text-center">
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider">Integrity Score</p>
                <p className={`text-2xl font-bold ${data.integrity_summary.score >= 80 ? 'text-emerald-700' : data.integrity_summary.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                  {data.integrity_summary.score}%
                </p>
              </div>
              <div className="p-3 bg-neutral-50 rounded-md text-center">
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider">Violations</p>
                <p className={`text-2xl font-bold ${data.integrity_summary.total_violations > 0 ? 'text-red-600' : 'text-neutral-900'}`}>
                  {data.integrity_summary.total_violations}
                </p>
              </div>
              <div className="p-3 bg-neutral-50 rounded-md text-center">
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider">Anomalies</p>
                <p className="text-2xl font-bold text-neutral-900">{data.integrity_summary.anomaly_responses}</p>
              </div>
              <div className="p-3 bg-neutral-50 rounded-md text-center">
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider">Too Slow (&gt;90s)</p>
                <p className={`text-2xl font-bold ${data.integrity_summary.slow_responses > 3 ? 'text-amber-600' : 'text-neutral-900'}`}>
                  {data.integrity_summary.slow_responses}
                </p>
              </div>
              <div className="p-3 bg-neutral-50 rounded-md text-center">
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider">Too Fast (&lt;3s)</p>
                <p className={`text-2xl font-bold ${data.integrity_summary.fast_responses > 5 ? 'text-red-600' : 'text-neutral-900'}`}>
                  {data.integrity_summary.fast_responses}
                </p>
              </div>
            </div>
            {Object.keys(data.integrity_summary.events_breakdown || {}).length > 0 && (
              <div>
                <p className="text-xs text-neutral-500 font-bold uppercase tracking-wider mb-2">Event Breakdown</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.integrity_summary.events_breakdown).map(([type, count]) => (
                    <span key={type} className="text-xs px-2 py-1 bg-red-50 text-red-700 rounded-sm font-mono">
                      {type.replace(/_/g, ' ')}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AI Analysis Sections */}
        {ai && !ai.error && (
          <>
            {!hasAnalysis && (
              <div className="bg-white rounded-lg border border-amber-200 p-6 mt-6" data-testid="no-analysis-panel">
                <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Written analysis not generated
                </h2>
                <p className="text-sm text-neutral-600 mb-1">
                  Everything above - the scores, consistency, red flags and chart - is complete
                  and was calculated from the candidate's answers. Only the written analysis is
                  missing, because automatic analysis is not switched on.
                </p>
                <p className="text-sm text-neutral-600 mb-4">
                  You can produce it yourself in two steps, and the result is the same:
                  copy the prompt, paste it into <span className="font-medium">claude.ai</span>,
                  then paste Claude's reply back here.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={copyAnalysisPrompt} className="bg-brand hover:bg-brand-dark text-white rounded-md" data-testid="copy-prompt-button">
                    1. Copy the prompt
                  </Button>
                  <Button variant="outline" onClick={() => setShowManual(!showManual)} className="rounded-md" data-testid="toggle-manual-button">
                    2. Paste the analysis
                  </Button>
                </div>

                {showManual && (
                  <div className="mt-4">
                    <textarea
                      id="manual-analysis"
                      value={manualText}
                      onChange={(e) => setManualText(e.target.value)}
                      rows={10}
                      placeholder="Paste Claude's full reply here, including the headings..."
                      className="w-full text-sm p-3 border border-neutral-300 rounded-md focus:outline-none focus:border-brand"
                      data-testid="manual-analysis-input"
                    />
                    <div className="flex gap-2 mt-2">
                      <Button onClick={submitManualAnalysis} disabled={savingManual} className="bg-brand hover:bg-brand-dark text-white rounded-md" data-testid="save-manual-button">
                        {savingManual ? 'Saving...' : 'Save analysis'}
                      </Button>
                      <Button variant="outline" onClick={() => { setShowManual(false); setManualText(''); }} className="rounded-md">
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-neutral-500 mt-2">
                      Keep Claude's five numbered headings so the sections land in the right
                      places on this report.
                    </p>
                  </div>
                )}
              </div>
            )}

            {ai.source === 'manual' && (
              <p className="text-xs text-neutral-500 mt-6" data-testid="manual-source-note">
                This analysis was added by hand{ai.added_by ? ` by ${ai.added_by}` : ''}.
              </p>
            )}

            {/* Contradiction Analysis */}
            {ai.contradiction_analysis && (
              <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
                <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Contradiction Analysis</h2>
                <AnalysisText>{ai.contradiction_analysis}</AnalysisText>
              </div>
            )}

            {/* Dimension Insights */}
            {ai.dimension_insights && (
              <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
                <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Dimension Insights</h2>
                <AnalysisText>{ai.dimension_insights}</AnalysisText>
              </div>
            )}

            {/* Personality Summary */}
            {ai.personality_summary && (
              <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
                <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Personality Summary</h2>
                <AnalysisText>{ai.personality_summary}</AnalysisText>
              </div>
            )}

            {/* Interview Questions */}
            {ai.interview_questions && (
              <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
                <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Recommended Interview Questions</h2>
                <AnalysisText>{ai.interview_questions}</AnalysisText>
              </div>
            )}

            {/* Final Recommendation Detail */}
            {ai.final_recommendation && (
              <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
                <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Final Recommendation</h2>
                <AnalysisText>{ai.final_recommendation}</AnalysisText>
              </div>
            )}
          </>
        )}

        {ai?.error && (
          <div className="bg-white rounded-lg border border-neutral-200 p-6 mt-6">
            <h2 className="text-lg font-semibold mb-3 text-amber-600">AI Analysis</h2>
            <p className="text-sm text-neutral-600">AI analysis could not be generated. Click "Regenerate AI" to try again.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportView;
